import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { apiClient, authStore } from "@/lib/apiClient";

const AuthContext = createContext(null);

/**
 * Intentional decision: the admin dashboard stores its JWT in localStorage.
 * This is an internal, admin-only surface with no user-generated HTML
 * being rendered — the XSS risk profile of an httpOnly cookie migration
 * does not justify the extra CORS/CSRF plumbing here.
 */
export function AuthProvider({ children }) {
  // undefined = checking, null = not authenticated, object = authenticated
  const [user, setUser] = useState(undefined);

  const refresh = useCallback(async () => {
    if (!authStore.getToken()) {
      setUser(null);
      return null;
    }
    try {
      const { data } = await apiClient.get("/auth/me");
      setUser(data);
      return data;
    } catch (_e) {
      authStore.clearToken();
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (username, password) => {
    const { data } = await apiClient.post("/auth/login", { username, password });
    authStore.setToken(data.access_token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    authStore.clearToken();
    setUser(null);
  };

  // Memoize the context value so that AuthContext consumers don't re-render
  // on every re-render of this provider.
  const value = useMemo(
    () => ({ user, login, logout, refresh }),
    [user, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
